import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Icon, useTheme } from '@Sage/hoi-poi-ui';
import { CopilotContext, CopilotSidebar } from '@Sage/ssm-web5';

import useCopilotBarScenarioMatch from 'components/CopilotBar/useCopilotBarScenarioMatch';
import useCopilot from 'hooks/useCopilot';
import SageCopilotIcon from 'lib/metadata/icons/SageCopilot';
import {
    COPILOT_BAR_HIDDEN_STORAGE_KEY,
    hasCopilotPanelStorageFlag,
    mergeCopilotBarOpenWithUserPreference,
    removeCopilotPanelStorageFlag,
    setCopilotPanelStorageFlag,
} from 'utils/copilotPanels';
import { getLiteral } from 'utils/getLiteral';

import './styles.scss';

const COPILOT_SIDEBAR_MENU_ON_CLASS = 'copilot-sidebar--on';
const COPILOT_SIDEBAR_LOCAL_STORAGE_KEY = 'CopilotSidebarOn';

const CopilotSidebarTrigger = ({ hasCopilotBar = false, hasCopilotSidebar = true }) => {
    const { copilotBarProps, setCopilotBarProps } = useContext(CopilotContext);
    const theme = useTheme();
    const { matchingScenario } = useCopilotBarScenarioMatch();
    const pendingDisclaimerTargetRef = useRef(null);

    const userData = useSelector((state) => state.config.userData || {});
    const copilotActivatedDisclaimer = useSelector(
        (state) => state.config.permission?.danaActivatedDisclaimer || false,
    );

    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        if (!hasCopilotSidebar) return false;

        return hasCopilotPanelStorageFlag(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY);
    });

    useEffect(() => {
        if (!hasCopilotSidebar) {
            setIsSidebarOpen(false);
        }
    }, [hasCopilotSidebar]);

    const openSidebarAfterDisclaimer = useCallback(() => {
        setIsSidebarOpen(true);
    }, []);

    const onConfirmDisclaimerSuccess = useCallback(() => {
        const target = pendingDisclaimerTargetRef.current;
        pendingDisclaimerTargetRef.current = null;
        if (target === 'bar' && setCopilotBarProps) {
            removeCopilotPanelStorageFlag(COPILOT_BAR_HIDDEN_STORAGE_KEY);
            setCopilotBarProps(
                mergeCopilotBarOpenWithUserPreference({
                    ...copilotBarProps,
                    isMinimized: false,
                    isOpen: true,
                }),
            );
        } else {
            openSidebarAfterDisclaimer();
        }
    }, [copilotBarProps, openSidebarAfterDisclaimer, setCopilotBarProps]);

    const { renderCopilotDisclaimer, setCopilotModalAfterDisclaimer } = useCopilot({
        onConfirmDisclaimerSuccess,
    });

    const triggerSidebar = useCallback(() => {
        if (isSidebarOpen) {
            setIsSidebarOpen(false);
        } else if (copilotActivatedDisclaimer) {
            setIsSidebarOpen(true);
        } else {
            pendingDisclaimerTargetRef.current = 'sidebar';
            setCopilotModalAfterDisclaimer(true);
        }
    }, [copilotActivatedDisclaimer, isSidebarOpen, setCopilotModalAfterDisclaimer]);

    const barScenarioReady = Boolean(matchingScenario);
    const barDisabled = !hasCopilotBar || !barScenarioReady;
    const { isMinimized: isBarMinimized, isOpen: isBarOpen } = copilotBarProps;
    const barExpanded = Boolean(isBarOpen && !isBarMinimized);

    const triggerBar = useCallback(() => {
        if (barDisabled || !setCopilotBarProps) return;

        if (isBarOpen && isBarMinimized) {
            setCopilotBarProps({ isMinimized: false });
            return;
        }

        if (isBarOpen && !isBarMinimized) {
            setCopilotPanelStorageFlag(COPILOT_BAR_HIDDEN_STORAGE_KEY);
            setCopilotBarProps(mergeCopilotBarOpenWithUserPreference(copilotBarProps));
            return;
        }

        if (!copilotActivatedDisclaimer) {
            pendingDisclaimerTargetRef.current = 'bar';
            setCopilotModalAfterDisclaimer(true);
            return;
        }

        removeCopilotPanelStorageFlag(COPILOT_BAR_HIDDEN_STORAGE_KEY);
        setCopilotBarProps(
            mergeCopilotBarOpenWithUserPreference({
                ...copilotBarProps,
                isMinimized: false,
                isOpen: true,
            }),
        );
    }, [
        barDisabled,
        copilotActivatedDisclaimer,
        copilotBarProps,
        isBarMinimized,
        isBarOpen,
        setCopilotBarProps,
        setCopilotModalAfterDisclaimer,
    ]);

    useEffect(() => {
        if (isSidebarOpen && !copilotActivatedDisclaimer) {
            setIsSidebarOpen(false);
        }
    }, [copilotActivatedDisclaimer, isSidebarOpen]);

    useEffect(() => {
        if (!hasCopilotSidebar) {
            document.body.classList.remove(COPILOT_SIDEBAR_MENU_ON_CLASS);
            return;
        }

        if (isSidebarOpen) {
            document.body.classList.add(COPILOT_SIDEBAR_MENU_ON_CLASS);
            setCopilotPanelStorageFlag(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY);
        } else {
            document.body.classList.remove(COPILOT_SIDEBAR_MENU_ON_CLASS);
            removeCopilotPanelStorageFlag(COPILOT_SIDEBAR_LOCAL_STORAGE_KEY);
        }
    }, [hasCopilotSidebar, isSidebarOpen]);

    const sidebarProps = useMemo(
        () => ({
            onClose: triggerSidebar,
            userData,
        }),
        [triggerSidebar, userData],
    );

    const iconActive = theme.colors.actionMinor[500];
    const iconGrey = theme.colors.actionMinor[200];

    const sidebarIconName = isSidebarOpen ? 'layoutPanelRight' : 'layoutPanelRightOutline';

    const barIconColor = barDisabled ? iconGrey : iconActive;
    const barIconName =
        barDisabled || !barExpanded ? 'layoutPanelBottomOutline' : 'layoutPanelBottom';

    return (
        <>
            <div className="copilot-sidebar-trigger">
                <span className="copilot-sidebar-trigger__logo" aria-hidden={true}>
                    <SageCopilotIcon />
                </span>
                <div className="copilot-panels-trigger">
                    {hasCopilotBar && (
                        <button
                            type="button"
                            className={classnames('copilot-panels-trigger__segment', {
                                'copilot-panels-trigger__segment--active':
                                    !barDisabled && barExpanded,
                                'copilot-panels-trigger__segment--bar-disabled': barDisabled,
                            })}
                            disabled={barDisabled}
                            onClick={triggerBar}
                        >
                            <Icon
                                color={barIconColor}
                                name={barIconName}
                                size="large"
                                tooltip={
                                    barDisabled ? undefined : getLiteral('label_copilot_toggle_bar')
                                }
                            />
                        </button>
                    )}
                    {hasCopilotSidebar && hasCopilotBar && (
                        <div className="copilot-panels-trigger__divider" aria-hidden />
                    )}
                    {hasCopilotSidebar && (
                        <button
                            type="button"
                            className={classnames('copilot-panels-trigger__segment', {
                                'copilot-panels-trigger__segment--active': isSidebarOpen,
                            })}
                            onClick={triggerSidebar}
                        >
                            <Icon
                                color={iconActive}
                                name={sidebarIconName}
                                size="large"
                                tooltip={getLiteral('label_copilot_toggle_sidebar')}
                            />
                        </button>
                    )}
                </div>
            </div>
            {hasCopilotSidebar && (
                <div
                    className={classnames('copilot-sidebar-container', {
                        'copilot-sidebar-container--opened': isSidebarOpen,
                    })}
                >
                    <CopilotSidebar {...sidebarProps} />
                </div>
            )}
            {renderCopilotDisclaimer}
        </>
    );
};

export default memo(CopilotSidebarTrigger);
